/** Two-row block font, in the spirit of figlet's `tiny`. Legibility over fidelity. */
const GLYPHS: Record<string, [string, string]> = {
  A: ['▄▀█', '█▀█'],
  B: ['█▄▄', '█▄█'],
  C: ['█▀▀', '█▄▄'],
  D: ['█▀▄', '█▄▀'],
  E: ['█▀▀', '██▄'],
  F: ['█▀▀', '█▀ '],
  G: ['█▀▀', '█▄█'],
  H: ['█ █', '█▀█'],
  I: ['█', '█'],
  J: [' █', '█▄█'],
  K: ['█▄▀', '█ █'],
  L: ['█  ', '█▄▄'],
  M: ['█▀▄▀█', '█ ▀ █'],
  N: ['█▄ █', '█ ▀█'],
  O: ['█▀█', '█▄█'],
  P: ['█▀█', '█▀▀'],
  Q: ['█▀█', '▀▀█'],
  R: ['█▀█', '█▀▄'],
  S: ['█▀', '▄█'],
  T: ['▀█▀', ' █ '],
  U: ['█ █', '█▄█'],
  V: ['█ █', '▀▄▀'],
  W: ['█ █ █', '▀▄▀▄▀'],
  X: ['▀▄▀', '█ █'],
  Y: ['█▄█', ' █ '],
  Z: ['▀█', '█▄'],
  '0': ['█▀█', '█▄█'],
  '1': ['▄█', ' █'],
  '2': ['▀▀█', '█▄▄'],
  '3': ['▀▀█', '▀▀█'],
  '4': ['█ █', '▀▀█'],
  '5': ['█▀', '▄█'],
  '6': ['█▀▀', '█▄█'],
  '7': ['▀▀█', '  █'],
  '8': ['▄▀▄', '▀▄▀'],
  '9': ['█▀█', '▀▀█'],
  '.': [' ', '▄'],
  '-': ['   ', '▀▀▀'],
  '/': [' ▄▀', '▀  '],
  '!': ['█', '▄'],
  '?': ['▀█', ' ▄'],
  ' ': ['  ', '  '],
}

/** Renders `value` as two rows of block characters, `figlet`-style. */
export function figlet(value: string): string[] {
  const glyphs = [...value.toUpperCase()].map((character) => GLYPHS[character]).filter(Boolean)
  if (!glyphs.length) return []
  return [0, 1].map((row) => glyphs.map((glyph) => glyph[row]).join(' ').trimEnd())
}

/** Speech bubble plus cow, sized to the longest line of `value`. */
export function cowsay(value: string): string[] {
  const body = value.split('\n')
  const width = Math.max(...body.map((row) => row.length))
  const bubble =
    body.length === 1
      ? [` ${'_'.repeat(width + 2)}`, `< ${body[0]} >`, ` ${'-'.repeat(width + 2)}`]
      : [
          ` ${'_'.repeat(width + 2)}`,
          ...body.map((row, index) => {
            const [open, close] =
              index === 0 ? ['/', '\\'] : index === body.length - 1 ? ['\\', '/'] : ['|', '|']
            return `${open} ${row.padEnd(width)} ${close}`
          }),
          ` ${'-'.repeat(width + 2)}`,
        ]
  return [
    ...bubble,
    '        \\   ^__^',
    '         \\  (oo)\\_______',
    '            (__)\\       )\\/\\',
    '                ||----w |',
    '                ||     ||',
  ]
}

/** Deterministic short hash, used for plausible-looking git object ids. */
export function shortHash(value: string): string {
  let hash = 0x811c9dc5
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index)
    hash = Math.imul(hash, 0x01000193) >>> 0
  }
  return hash.toString(16).padStart(8, '0').slice(0, 7)
}
